// Camera-roll ingest: multi-select photos from the device library, detect every
// garment in each, review and correct the detected metadata, then generate a
// clean transparent-background cutout per garment and save the approved ones as
// closet items.
//
// Cutouts are generated on demand AFTER the metadata is confirmed — the cutout
// prompt is anchored on the (now user-confirmed) name/category/color, so a wrong
// detection can't misdirect the render. The review session is entirely
// client-side (in memory): detection and cutout are stateless API calls, and
// approval reuses POST /api/items like any other add flow. A page reload
// mid-review discards unsaved drafts.

import { useRef, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Images, Sparkles, Check, RotateCcw, Trash2, Shirt } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Button, Spinner, EmptyState, Field, inputClass } from '@/components/ui';
import { ComboSelect } from '@/features/wardrobe/components/ComboSelect';
import { downscaleToBase64, cropToBase64, type BoundingBox } from '@/lib/image';
import { ITEM_CATEGORIES, COLORS, COLOR_HEX } from '@/lib/constants';
import api, { ApiError } from '@/lib/api';

const COLOR_NAMES = COLORS.map((c) => c.name);

interface DetectedGarment {
  name: string;
  category: string;
  color: string;
  colorHex: string;
  secondaryColorHex: string | null;
  tags: string[];
  boundingBox: BoundingBox;
}

type DraftStatus = 'pending' | 'generating' | 'ready' | 'approving' | 'error';

interface Draft {
  id: string;
  crop: string; // cropped garment (jpeg data URL)
  cutout: string | null; // transparent PNG data URL, once generated
  name: string;
  category: string;
  color: string;
  colorHex: string;
  secondaryColorHex: string | null;
  tags: string[];
  boundingBox: BoundingBox;
  status: DraftStatus;
  error?: string;
}

// Run an async task over items with a bounded concurrency.
async function runPool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const idx = cursor++;
      await fn(items[idx]);
    }
  });
  await Promise.all(workers);
}

export function CameraRollImportPage() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [cutoutsEnabled, setCutoutsEnabled] = useState(true);
  const [approvingAll, setApprovingAll] = useState(false);
  const [imported, setImported] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const patch = (id: string, next: Partial<Draft>) =>
    setDrafts((prev) => prev.map((d) => (d.id === id ? { ...d, ...next } : d)));
  const remove = (id: string) => setDrafts((prev) => prev.filter((d) => d.id !== id));

  // --- pick + detect ---------------------------------------------------------
  const handleFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    setError(null);
    setDetecting(true);
    setProgress({ done: 0, total: files.length });

    await runPool(files, 3, async (file) => {
      try {
        const image = await downscaleToBase64(file);
        const { garments } = await api.post<{ garments: DetectedGarment[] }>('/ai/detect-garments', { image });
        const made: Draft[] = [];
        for (const g of garments) {
          const crop = await cropToBase64(image, g.boundingBox);
          made.push({
            id: crypto.randomUUID(),
            crop,
            cutout: null,
            name: g.name,
            category: g.category,
            color: g.color,
            colorHex: g.colorHex,
            secondaryColorHex: g.secondaryColorHex,
            tags: g.tags ?? [],
            boundingBox: g.boundingBox,
            status: 'pending',
          });
        }
        if (made.length) setDrafts((prev) => [...prev, ...made]);
      } catch {
        // One bad photo shouldn't sink the batch; surface a soft note.
        setError('Some photos could not be analyzed and were skipped.');
      } finally {
        setProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      }
    });

    // Cutouts are generated later, once the user has confirmed each garment's
    // metadata — via the per-card "Generate cutout" / "Generate all" actions.
    setDetecting(false);
    setProgress(null);
  };

  // --- cutout generation -----------------------------------------------------
  const generateCutouts = async (targets: Draft[]) => {
    if (!cutoutsEnabled) return;
    let disabled = false;
    await runPool(targets, 2, async (d) => {
      if (disabled) return;
      patch(d.id, { status: 'generating', error: undefined });
      try {
        const { image } = await api.post<{ image: string }>('/ai/cutout', {
          image: d.crop,
          metadata: {
            name: d.name,
            category: d.category,
            colorHex: d.colorHex,
            secondaryColorHex: d.secondaryColorHex,
            tags: d.tags,
          },
        });
        patch(d.id, { cutout: image, status: 'ready' });
      } catch (err) {
        if (err instanceof ApiError && err.status === 503) {
          // Cutouts aren't configured — fall back to plain crops for the batch.
          disabled = true;
          setCutoutsEnabled(false);
          patch(d.id, { status: 'ready' });
        } else {
          patch(d.id, { status: 'error', error: err instanceof Error ? err.message : 'Cutout failed' });
        }
      }
    });
  };

  const regenerate = (d: Draft) => generateCutouts([{ ...d, cutout: null }]);
  const generateAll = () => generateCutouts(drafts.filter((d) => d.status === 'pending'));

  // --- approve / reject ------------------------------------------------------
  const approve = async (d: Draft): Promise<boolean> => {
    patch(d.id, { status: 'approving', error: undefined });
    try {
      await api.post('/items', {
        name: d.name.trim() || 'New Item',
        category: d.category || 'Accessories',
        color: d.color || undefined,
        colorHex: d.colorHex || undefined,
        status: 'in-closet',
        photos: [{ image: d.cutout ?? d.crop, kind: 'item' }],
        aiIntakeData: {
          source: 'camera-roll',
          detectedColorHex: d.colorHex,
          tags: d.tags,
          boundingBox: d.boundingBox,
          hadCutout: !!d.cutout,
        },
        intakeConfirmed: true,
      });
      remove(d.id);
      setImported((c) => c + 1);
      return true;
    } catch {
      patch(d.id, { status: 'error', error: 'Could not save this item.' });
      return false;
    }
  };

  const approveAll = async () => {
    const ready = drafts.filter(canApprove);
    if (ready.length === 0) return;
    setApprovingAll(true);
    await runPool(ready, 2, async (d) => {
      await approve(d);
    });
    setApprovingAll(false);
  };

  // A draft is approvable once its cutout exists, or immediately (with the raw
  // crop) when cutouts aren't available. Generation is offered only while a
  // cutout is still pending and cutouts are enabled.
  const canApprove = (d: Draft) => d.cutout !== null || (!cutoutsEnabled && d.status !== 'approving');
  const readyCount = drafts.filter(canApprove).length;
  const pendingCount = cutoutsEnabled ? drafts.filter((d) => d.status === 'pending').length : 0;
  const anyGenerating = drafts.some((d) => d.status === 'generating');
  const busy = detecting || approvingAll || anyGenerating;

  return (
    <Page>
      <PageHeader section="Import from Camera Roll" backHref="/camera" backLabel="Camera" />
      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {error && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {!cutoutsEnabled && (
          <div className="rounded-xl bg-secondary/60 text-muted-foreground px-4 py-3 text-xs">
            Cutout generation isn't enabled (no OpenAI key). Importing the cropped photos as-is —
            you can still add cutouts later.
          </div>
        )}

        {/* Picker */}
        <label className="block cursor-pointer">
          <div className="flex items-center justify-center gap-2 w-full py-4 bg-primary text-primary-foreground rounded-xl text-sm font-semibold">
            <Images className="w-5 h-5" />
            {drafts.length ? 'Add more photos' : 'Choose photos from camera roll'}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
            disabled={busy}
          />
        </label>

        {detecting && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
            <Spinner className="w-4 h-4" />
            Detecting garments{progress ? ` (${progress.done}/${progress.total} photos)` : '…'}
          </div>
        )}

        {imported > 0 && (
          <div className="flex items-center justify-between bg-green-100 text-green-800 rounded-xl px-4 py-3 text-sm">
            <span className="font-medium">
              {imported} item{imported === 1 ? '' : 's'} added to your wardrobe
            </span>
            <Button variant="outline" className="py-2 px-3" onClick={() => navigate('/wardrobe/items')}>
              View
            </Button>
          </div>
        )}

        {/* Empty state */}
        {drafts.length === 0 && !detecting && (
          <EmptyState
            icon={<Shirt className="w-6 h-6" />}
            title="No garments yet"
            description="Pick a few outfit or item photos and each garment will be detected and turned into a clean product cutout for review."
          />
        )}

        {/* Batch action bar */}
        {(pendingCount > 0 || readyCount > 0) && (
          <div className="flex items-center justify-between gap-3 sticky top-2 z-10 bg-background/80 backdrop-blur-sm rounded-xl px-1 py-1">
            <span className="text-sm text-muted-foreground">
              {pendingCount > 0 ? `${pendingCount} to generate` : `${readyCount} ready to add`}
            </span>
            <div className="flex items-center gap-2">
              {pendingCount > 0 && (
                <Button variant={readyCount > 0 ? 'secondary' : undefined} onClick={generateAll} disabled={busy}>
                  <Sparkles className="w-4 h-4" />
                  {`Generate all (${pendingCount})`}
                </Button>
              )}
              {readyCount > 0 && (
                <Button onClick={approveAll} disabled={busy}>
                  {approvingAll ? <Spinner className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                  {approvingAll ? 'Adding…' : `Approve all (${readyCount})`}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Review grid */}
        <div className="space-y-4">
          {drafts.map((d) => (
            <DraftCard
              key={d.id}
              draft={d}
              cutoutsEnabled={cutoutsEnabled}
              approvable={canApprove(d)}
              onChange={(next) => patch(d.id, next)}
              onApprove={() => approve(d)}
              onReject={() => remove(d.id)}
              onGenerate={() => regenerate(d)}
              disabled={busy}
            />
          ))}
        </div>
      </div>
    </Page>
  );
}

function DraftCard({
  draft: d,
  cutoutsEnabled,
  approvable,
  onChange,
  onApprove,
  onReject,
  onGenerate,
  disabled,
}: {
  draft: Draft;
  cutoutsEnabled: boolean;
  approvable: boolean;
  onChange: (next: Partial<Draft>) => void;
  onApprove: () => void;
  onReject: () => void;
  onGenerate: () => void;
  disabled: boolean;
}) {
  const busy = d.status === 'generating' || d.status === 'approving' || disabled;
  const preview = d.cutout ?? d.crop;
  const hasCutout = d.cutout !== null;
  // Offer generation until a cutout exists; once it does, the primary action is
  // "Add to wardrobe" and regeneration moves to the secondary button.
  const showGenerate = cutoutsEnabled && !hasCutout;

  return (
    <div className="rounded-2xl bg-card border border-border overflow-hidden">
      {/* Image — object-contain on a neutral field so the whole garment reads,
          the way a product cutout should. */}
      <div className="relative aspect-square bg-secondary flex items-center justify-center">
        <img src={preview} alt={d.name} className="w-full h-full object-contain" />
        {d.status === 'generating' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/60 backdrop-blur-sm">
            <Sparkles className="w-7 h-7 text-primary animate-pulse" />
            <span className="text-xs text-muted-foreground">Generating cutout…</span>
          </div>
        )}
        {d.cutout && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wide bg-primary/10 text-primary px-2 py-0.5 rounded-full">
            Cutout
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        <Field label="Name">
          <input
            type="text"
            value={d.name}
            onChange={(e) => onChange({ name: e.target.value })}
            className={inputClass}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <ComboSelect
              value={d.category}
              options={[...ITEM_CATEGORIES]}
              onChange={(v) => onChange({ category: v })}
              placeholder="Category"
            />
          </Field>
          <Field label="Color">
            <ComboSelect
              value={d.color}
              options={COLOR_NAMES}
              onChange={(v) => onChange({ color: v, colorHex: COLOR_HEX[v] ?? d.colorHex })}
              placeholder="Color"
            />
          </Field>
        </div>

        {d.status === 'error' && d.error && (
          <p className="text-xs text-destructive">{d.error}</p>
        )}

        <div className="flex items-center gap-2 pt-1">
          <Button variant="outline" className="px-3" onClick={onReject} disabled={d.status === 'approving'} aria-label="Reject" title="Discard">
            <Trash2 className="w-4 h-4" />
          </Button>
          {hasCutout && (
            <Button
              variant="secondary"
              className="px-3"
              onClick={onGenerate}
              disabled={busy}
              aria-label="Regenerate cutout"
              title="Regenerate cutout"
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
          {showGenerate ? (
            <Button fullWidth onClick={onGenerate} disabled={busy}>
              {d.status === 'generating' ? <Spinner className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
              {d.status === 'generating'
                ? 'Generating…'
                : d.status === 'error'
                  ? 'Try again'
                  : 'Generate cutout'}
            </Button>
          ) : (
            <Button fullWidth onClick={onApprove} disabled={busy || !approvable}>
              {d.status === 'approving' ? <Spinner className="w-4 h-4" /> : <Check className="w-4 h-4" />}
              {d.status === 'approving' ? 'Adding…' : 'Add to wardrobe'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
